import React, { useEffect, useState, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Hourglass, ChatCircleDots, Receipt } from '@phosphor-icons/react';
import { API, formatPrice } from '../utils/marketplace';

export default function MarketplaceOrderSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const orderId = params.get('order_id');
  const [state, setState] = useState('checking'); // checking | paid | timeout | error
  const [order, setOrder] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId || !orderId) { setState('error'); return; }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const res = await axios.post(`${API}/api/marketplace/orders/verify`, null, {
          params: { session_id: sessionId, order_id: orderId },
        });
        if (res.data.success && res.data.status === 'paid') {
          setOrder(res.data.order);
          setState('paid');
          return;
        }
      } catch {
        setState('error');
        return;
      }
      if (attempts.current >= 10) { setState('timeout'); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, orderId]);

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center px-4" data-testid="order-success-page">
      <div className="w-full max-w-md bg-white dark:bg-neutral-800 border-2 border-black rounded-2xl shadow-[6px_6px_0px_#000] p-8 text-center">
        {state === 'checking' && (
          <>
            <Hourglass size={48} weight="duotone" className="text-[#D95800] mx-auto mb-4 animate-pulse" />
            <h1 className="text-xl font-black uppercase text-neutral-900 dark:text-white">Confirming payment...</h1>
            <p className="text-sm text-neutral-500 mt-2">Hang tight, this takes a few seconds.</p>
          </>
        )}
        {state === 'paid' && order && (
          <>
            <CheckCircle size={56} weight="fill" className="text-emerald-500 mx-auto mb-4" />
            <h1 className="text-2xl font-black uppercase text-neutral-900 dark:text-white" data-testid="order-success-title">It's yours!</h1>
            <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-2">
              You bought <span className="font-bold">{order.listing_title}</span> for{' '}
              <span className="font-black text-[#D95800]">{formatPrice(order.amount)}</span>.
            </p>
            <p className="text-xs text-neutral-500 mt-2">
              We've opened a chat with the seller so you can arrange pickup or delivery.
            </p>
            <div className="flex flex-col gap-2 mt-6">
              <Link
                to={`/messages/${order.seller_id}`}
                className="inline-flex items-center justify-center gap-2 bg-[#D95800] text-white font-black uppercase text-sm py-3 border-2 border-black shadow-[3px_3px_0px_#000]"
                data-testid="order-success-message-seller"
              >
                <ChatCircleDots size={16} weight="fill" /> Message Seller
              </Link>
              <Link
                to="/marketplace/orders"
                className="inline-flex items-center justify-center gap-2 bg-white dark:bg-neutral-900 text-neutral-800 dark:text-neutral-200 font-black uppercase text-sm py-3 border-2 border-black"
                data-testid="order-success-view-orders"
              >
                <Receipt size={16} weight="bold" /> My Orders
              </Link>
            </div>
          </>
        )}
        {state === 'timeout' && (
          <>
            <Hourglass size={48} weight="duotone" className="text-amber-500 mx-auto mb-4" />
            <h1 className="text-xl font-black uppercase text-neutral-900 dark:text-white">Still processing</h1>
            <p className="text-sm text-neutral-500 mt-2">Your payment is taking longer than usual. Check your orders in a minute.</p>
            <Link to="/marketplace/orders" className="mt-5 inline-block bg-[#2B4C6F] text-white font-black uppercase text-sm px-6 py-3 border-2 border-black">My Orders</Link>
          </>
        )}
        {state === 'error' && (
          <>
            <h1 className="text-xl font-black uppercase text-neutral-900 dark:text-white">Something went wrong</h1>
            <p className="text-sm text-neutral-500 mt-2">We couldn't verify this payment. If you were charged, contact support.</p>
            <Link to="/marketplace" className="mt-5 inline-block bg-[#2B4C6F] text-white font-black uppercase text-sm px-6 py-3 border-2 border-black">Back to Marketplace</Link>
          </>
        )}
      </div>
    </div>
  );
}
